/**
 * useWebRTC.ts — WebRTC 연결 훅
 * GenomicAI/shared/hooks/useWebRTC.ts 에서 import path만 조정하여 복사
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DataSnapshot } from 'firebase/database';
import { db } from '../services/rtdb';
import { ref, set, push, get, onValue, onChildAdded, remove, off } from 'firebase/database';

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

const log = {
  rtc: (msg: string, ...args: unknown[]) => console.log(`[WebRTC] ${msg}`, ...args),
  ice: (msg: string, ...args: unknown[]) => console.log(`[ICE] ${msg}`, ...args),
  fb:  (msg: string, ...args: unknown[]) => console.log(`[Firebase] ${msg}`, ...args),
  err: (msg: string, ...args: unknown[]) => console.error(`[WebRTC ERR] ${msg}`, ...args),
};

export type ConnState = RTCPeerConnectionState | 'idle';

export interface UseWebRTCReturn {
  remoteStream: MediaStream | null;
  connState: ConnState;
  createRoom: (code: string, stream: MediaStream, rtdbPath?: string) => Promise<void>;
  joinRoom:   (code: string, stream: MediaStream, rtdbPath?: string) => Promise<void>;
  hangup:     (code: string, rtdbPath?: string) => Promise<void>;
  replaceVideoTrack: (track: MediaStreamTrack | null) => void;
}

const DEFAULT_PATH = 'land-consults';

export function useWebRTC(): UseWebRTCReturn {
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [connState, setConnState]       = useState<ConnState>('idle');

  const pcRef             = useRef<RTCPeerConnection | null>(null);
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([]);
  const unsubscribers     = useRef<Array<() => void>>([]);

  const flushUnsubscribers = useCallback(() => {
    unsubscribers.current.forEach(fn => fn());
    unsubscribers.current = [];
  }, []);

  const createPC = useCallback((stream: MediaStream): RTCPeerConnection => {
    pcRef.current?.close();
    pendingCandidates.current = [];

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const rs = new MediaStream();

    pc.ontrack = e => {
      rs.addTrack(e.track);
      setRemoteStream(new MediaStream(rs.getTracks()));
    };
    pc.onconnectionstatechange    = () => { log.rtc('연결 상태:', pc.connectionState); setConnState(pc.connectionState); };
    pc.oniceconnectionstatechange = () => log.ice('ICE 상태:', pc.iceConnectionState);

    stream.getTracks().forEach(t => pc.addTrack(t, stream));
    pcRef.current = pc;
    return pc;
  }, []);

  const createRoom = useCallback(async (
    code: string,
    stream: MediaStream,
    rtdbPath = DEFAULT_PATH,
  ): Promise<void> => {
    const pc = createPC(stream);
    const base = `${rtdbPath}/${code}`;

    pc.onicecandidate = e => {
      if (e.candidate) push(ref(db, `${base}/offerCandidates`), e.candidate.toJSON());
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await set(ref(db, base), {
      offer: { type: offer.type, sdp: offer.sdp },
      status: 'waiting',
      createdAt: Date.now(),
    });

    const answerRef = ref(db, `${base}/answer`);
    const unsubAnswer = onValue(answerRef, async (snap: DataSnapshot) => {
      if (!snap.exists() || pc.currentRemoteDescription) return;
      await pc.setRemoteDescription(new RTCSessionDescription(snap.val() as RTCSessionDescriptionInit));
      for (const c of pendingCandidates.current) {
        await pc.addIceCandidate(new RTCIceCandidate(c)).catch(e => log.err('ICE', e));
      }
      pendingCandidates.current = [];
      await set(ref(db, `${base}/status`), 'connected');
    });
    unsubscribers.current.push(() => off(answerRef), unsubAnswer);

    const answerCandRef = ref(db, `${base}/answerCandidates`);
    const unsubCand = onChildAdded(answerCandRef, async (snap: DataSnapshot) => {
      const c = snap.val() as RTCIceCandidateInit | null;
      if (!c) return;
      if (pc.currentRemoteDescription) {
        await pc.addIceCandidate(new RTCIceCandidate(c)).catch(e => log.err('ICE', e));
      } else {
        pendingCandidates.current.push(c);
      }
    });
    unsubscribers.current.push(() => off(answerCandRef), unsubCand);

    const statusRef = ref(db, `${base}/status`);
    const unsubStatus = onValue(statusRef, (snap: DataSnapshot) => {
      if (snap.val() === 'ended') { setConnState('closed'); setRemoteStream(null); }
    });
    unsubscribers.current.push(() => off(statusRef), unsubStatus);
  }, [createPC]);

  const joinRoom = useCallback(async (
    code: string,
    stream: MediaStream,
    rtdbPath = DEFAULT_PATH,
  ): Promise<void> => {
    const base = `${rtdbPath}/${code}`;

    const offerSnap = await get(ref(db, `${base}/offer`));
    if (!offerSnap.exists()) throw new Error('방을 찾을 수 없습니다.');

    const pc = createPC(stream);

    pc.onicecandidate = e => {
      if (e.candidate) push(ref(db, `${base}/answerCandidates`), e.candidate.toJSON());
    };

    await pc.setRemoteDescription(new RTCSessionDescription(offerSnap.val() as RTCSessionDescriptionInit));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await set(ref(db, `${base}/answer`), { type: answer.type, sdp: answer.sdp });

    const offerCandRef = ref(db, `${base}/offerCandidates`);
    const unsubCand = onChildAdded(offerCandRef, async (snap: DataSnapshot) => {
      const c = snap.val() as RTCIceCandidateInit | null;
      if (c) await pc.addIceCandidate(new RTCIceCandidate(c)).catch(e => log.err('ICE', e));
    });
    unsubscribers.current.push(() => off(offerCandRef), unsubCand);

    const statusRef = ref(db, `${base}/status`);
    const unsubStatus = onValue(statusRef, (snap: DataSnapshot) => {
      if (snap.val() === 'ended') { setConnState('closed'); setRemoteStream(null); }
    });
    unsubscribers.current.push(() => off(statusRef), unsubStatus);
  }, [createPC]);

  const hangup = useCallback(async (code: string, rtdbPath = DEFAULT_PATH): Promise<void> => {
    flushUnsubscribers();
    pcRef.current?.close();
    pcRef.current = null;
    pendingCandidates.current = [];
    setRemoteStream(null);
    setConnState('closed');
    const base = `${rtdbPath}/${code}`;
    await set(ref(db, `${base}/status`), 'ended');
    setTimeout(() => remove(ref(db, base)), 5000);
  }, [flushUnsubscribers]);

  const replaceVideoTrack = useCallback((track: MediaStreamTrack | null) => {
    const sender = pcRef.current?.getSenders().find(s => s.track?.kind === 'video');
    if (sender) sender.replaceTrack(track);
  }, []);

  useEffect(() => () => { flushUnsubscribers(); pcRef.current?.close(); }, [flushUnsubscribers]);

  return { remoteStream, connState, createRoom, joinRoom, hangup, replaceVideoTrack };
}
